import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoginForm } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useLogin,
  useRegister,
  useConfirmEmail,
  useResendConfirmation,
  useForgotPassword,
  useResetPassword,
} from "@/hooks/useAuth";
import { AuthDialogProps } from "@/types/componentsTypes";
import { useAuthStore } from "@/stores/authStore";

type AuthForm = "login" | "register" | "forgot-password" | "reset-password";

export function AuthDialog({
  children,
  defaultForm = "login",
  defaultOpen = false,
  resetData,
}: AuthDialogProps) {
  const { t } = useLanguage();

  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const [currentForm, setCurrentForm] = React.useState<AuthForm>(defaultForm);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmEmail, setConfirmEmail] = React.useState<string>("");
  const [token, setToken] = React.useState("");

  const [showConfirmSuccess, setShowConfirmSuccess] = React.useState(false);

  const confirmMutation = useConfirmEmail();
  const resendMutation = useResendConfirmation();
  const forgotPasswordMutation = useForgotPassword();
  const resetPasswordMutation = useResetPassword();

  const resetConfirmModalState = () => {
    setToken("");
    confirmMutation.reset();
    resendMutation.reset();
  };

  const openConfirmModal = (emailAddress: string) => {
    setConfirmEmail(emailAddress);
    resetConfirmModalState();
    setConfirmOpen(true);
  };

  const handleConfirmOpenChange = (open: boolean) => {
    setConfirmOpen(open);

    if (!open) {
      resetConfirmModalState();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setCurrentForm(defaultForm);
      useAuthStore.getState().setError(null);
    }
  }, [isOpen, defaultForm]);

  const loginMutation = useLogin({
    onEmailNotConfirmed: (emailAddress) => {
      openConfirmModal(emailAddress);
    },
  });

  const registerMutation = useRegister({
    onNeedsEmailConfirmation: ({ emailAddress }) => {
      openConfirmModal(emailAddress);
    },
  });

  const handleLoginSubmit = (data: {
    emailAddress: string;
    password: string;
  }) => {
    loginMutation.mutate(data, {
      onSuccess: () => {
        setIsOpen(false);
        setShowConfirmSuccess(false);
      },
      onError: (err) => {
        console.error("[AuthDialog] login error:", err);
      },
    });
  };

  const handleRegisterSubmit = (data: {
    emailAddress: string;
    firstName: string;
    lastName: string;
    password: string;
  }) => {
    registerMutation.mutate(data, {
      onSuccess: () => {
        setIsOpen(false);
      },
      onError: (err) => {
        console.error("[AuthDialog] register error:", err);
      },
    });
  };

  const toggleForm = () => {
    useAuthStore.getState().setError(null);
    setShowConfirmSuccess(false);
    setCurrentForm((c) => (c === "login" ? "register" : "login"));
  };

  const goToLogin = () => {
    useAuthStore.getState().setError(null);
    setCurrentForm("login");
  };

  const goToForgotPassword = () => {
    useAuthStore.getState().setError(null);
    setShowConfirmSuccess(false);
    setCurrentForm("forgot-password");
  };

  const goToResetPassword = () => {
    useAuthStore.getState().setError(null);
    setShowConfirmSuccess(false);
    setCurrentForm("reset-password");
  };

  const handleForgotPasswordSubmit = async (email: string) => {
    await forgotPasswordMutation.mutateAsync(email);
  };

  const handleResetPasswordSubmit = async (data: {
    email: string;
    resetToken: string;
    newPassword: string;
  }) => {
    await resetPasswordMutation.mutateAsync(data);
  };

  const submitToken = async () => {
    await confirmMutation.mutateAsync(token);

    setShowConfirmSuccess(true);
    setConfirmOpen(false);
    resetConfirmModalState();

    setCurrentForm("login");
    setIsOpen(true);
  };

  const resendEmail = async () => {
    await resendMutation.mutateAsync(confirmEmail);
  };

  const renderForm = () => {
    switch (currentForm) {
      case "login":
        return (
          <>
            {showConfirmSuccess && (
              <p className="mb-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
                {t("confirmSuccess")}
              </p>
            )}

            <LoginForm
              onToggleToRegister={toggleForm}
              onForgotPassword={goToForgotPassword}
              onSubmit={handleLoginSubmit}
            />
          </>
        );

      case "register":
        return (
          <RegisterForm
            onToggleToLogin={toggleForm}
            onSubmit={handleRegisterSubmit}
          />
        );

      case "forgot-password":
        return (
          <>
            <ForgotPasswordForm
              onBackToLogin={goToLogin}
              onSubmit={handleForgotPasswordSubmit}
              isPending={forgotPasswordMutation.isPending}
              isSuccess={forgotPasswordMutation.isSuccess}
            />

            {forgotPasswordMutation.isSuccess && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={goToResetPassword}
                  className="text-sm text-primary hover:text-primary/80 transition-colors duration-200 hover:underline"
                >
                  {t("haveResetToken")}
                </button>
              </div>
            )}
          </>
        );

      case "reset-password":
        return (
          <ResetPasswordForm
            onBackToLogin={goToLogin}
            onSubmit={handleResetPasswordSubmit}
            isPending={resetPasswordMutation.isPending}
            isSuccess={resetPasswordMutation.isSuccess}
            errorMessage={
              resetPasswordMutation.isError ? t("resetPasswordError") : null
            }
            initialEmail={resetData?.email}
            initialToken={resetData?.token}
          />
        );
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>{children}</DialogTrigger>

        <DialogContent
          className="sm:max-w-md w-full max-w-sm mx-auto p-0 overflow-hidden animate-scale-in border-0 shadow-2xl"
          onInteractOutside={(event) => {
            event.preventDefault();
          }}
        >
          <DialogTitle className="sr-only">{t("authDialogTitle")}</DialogTitle>

          <DialogDescription className="sr-only">
            {t("authDialogDescription")}
          </DialogDescription>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-background to-accent/10" />

            <div className="relative p-8">
              {renderForm()}

              {(loginMutation.isPending || registerMutation.isPending) && (
                <p className="mt-3 text-sm text-muted-foreground">
                  {t("processing")}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={handleConfirmOpenChange}>
        <DialogContent
          className="w-[90vw] max-w-[25rem] p-5"
          onInteractOutside={(event) => {
            event.preventDefault();
          }}
        >
          <DialogTitle>{t("confirmEmailTitle")}</DialogTitle>

          <DialogDescription>
            {t("confirmEmailDescription").replace("{email}", confirmEmail)}
          </DialogDescription>

          <div className="space-y-3 pt-4">
            <Input
              placeholder={t("confirmationTokenPlaceholder")}
              value={token}
              className="pl-2"
              onChange={(e) => setToken(e.target.value)}
            />

            <div className="flex gap-2">
              <Button
                onClick={submitToken}
                disabled={confirmMutation.isPending}
              >
                {t("confirm")}
              </Button>

              <Button
                variant="secondary"
                onClick={resendEmail}
                disabled={resendMutation.isPending}
                title={t("resendConfirmationHelp")}
              >
                {t("resendConfirmation")}
              </Button>
            </div>

            {resendMutation.isSuccess && (
              <p className="text-sm text-green-600">{t("resendSuccess")}</p>
            )}

            {confirmMutation.isSuccess && (
              <p className="text-sm text-green-600">{t("confirmSuccess")}</p>
            )}

            {(confirmMutation.isError || resendMutation.isError) && (
              <p className="text-sm text-red-600">{t("genericError")}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
